/**
 * Gradebook computation — the pure maths behind the term grid.
 *
 * Design: knowledge/wiki/features/marks-gradebook.md ("Computation"). These helpers turn
 * raw mark events + a subject's assessment columns + a grade scale into the
 * numbers the grid shows. No Firestore, no React — pure and unit-tested.
 *
 *   columnPct(event)    = numericValue / maxScore * 100
 *   classAvg(column)    = simple mean of column percentages   → the average ROW
 *   studentSubjectTotal = Σ(pct × weight) / Σ(weight)         → Grade + average COLUMN
 *   gradeForTotal()     = lives in gradeScalesService (re-exported here for convenience)
 *
 * A "cell" is the latest mark event matching (studentId, subjectId, column.key)
 * within the active term. Resolving that latest event is the caller's job; these
 * helpers operate on the values once resolved.
 */

import { gradeForTotal } from '../../../services/gradeScalesService';

export { gradeForTotal };

/** Is this a usable numeric value (not null/undefined/NaN)? */
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

/**
 * A single mark as a percentage of its "out of".
 * Returns null when the mark is missing or the max is non-positive — callers
 * treat null as "no mark", never as 0.
 */
export function columnPct(numericValue, maxScore) {
  // Reject "no mark" explicitly first — Number(null) is 0, which would otherwise
  // masquerade as a real zero mark.
  if (numericValue == null || numericValue === '') return null;
  const n = Number(numericValue);
  const max = Number(maxScore);
  if (!isNum(n) || !isNum(max) || max <= 0) return null;
  return (n / max) * 100;
}

/**
 * Class average for one column — a plain mean of the present percentages.
 * Missing marks are excluded (not counted as 0). Returns null when the column
 * has no marks at all.
 *
 * @param {Array<number|null>} pcts - columnPct() results for each student
 */
export function classAvg(pcts) {
  const present = (pcts || []).filter(isNum);
  if (present.length === 0) return null;
  return present.reduce((sum, p) => sum + p, 0) / present.length;
}

/**
 * A student's weighted total across one subject's columns.
 *
 * @param {Array<{ pct: number|null, weight?: number }>} entries
 *   one per assessment column for this student (pct = columnPct of their mark).
 * @returns {{ total: number|null, complete: boolean, missing: number }}
 *   - total: weighted mean of present marks, or null if none present
 *   - complete: true only when every required column has a mark
 *   - missing: how many required columns lack a mark
 *
 * A missing required column does NOT silently shrink the denominator-of-record:
 * `total` is computed over present columns so the grid can still show a provisional
 * figure, but `complete=false` lets the UI flag it as an incomplete total.
 */
export function studentSubjectTotal(entries) {
  const cols = entries || [];
  const present = cols.filter((e) => isNum(e?.pct));
  const missing = cols.length - present.length;

  if (present.length === 0) {
    return { total: null, complete: cols.length === 0, missing };
  }

  let weightedSum = 0;
  let weightTotal = 0;
  for (const e of present) {
    const w = e.weight == null ? 1 : Number(e.weight);
    const weight = isNum(w) && w >= 0 ? w : 1;
    weightedSum += e.pct * weight;
    weightTotal += weight;
  }

  // All present weights zero → fall back to a simple mean so the row isn't blank.
  const total = weightTotal > 0
    ? weightedSum / weightTotal
    : present.reduce((s, e) => s + e.pct, 0) / present.length;

  return { total, complete: missing === 0, missing };
}
